import { DateTime } from 'luxon'

type DateType = string | number | Date | DateTime

const isMilliseconds = (number: number) => {
  return number > 9999999999
}

export const parseDate = (date: DateType) => {
  if (typeof date === 'string') {
    return DateTime.fromISO(date)
  }

  if (typeof date === 'number') {
    return isMilliseconds(date)
      ? DateTime.fromMillis(date)
      : DateTime.fromSeconds(date)
  }

  if (date instanceof Date) {
    return DateTime.fromJSDate(date)
  }

  if (date instanceof DateTime) {
    return date
  }

  throw new Error('Invalid date')
}

export const formatDate = (
  date: DateType | null | undefined,
  stringFormat = "MMMM dd, yyyy 'at' t ZZZZ",
) => {
  if (date === null || date === undefined) return '-'
  return parseDate(date).toFormat(stringFormat)
}

export const formatRelativeDate = (date: DateType | null | undefined) => {
  if (date === null || date === undefined) return '-'
  return parseDate(date).toRelative()
}

export const groupByDateFormat = <T>(
  dates: T[],
  key: keyof T,
  format: string,
) => {
  return dates.reduce(
    (accumulator: Record<string, T[]>, date: T) => {
      const formattedDate = formatDate(date[key] as unknown as DateType, format)
      if (!accumulator[formattedDate]) {
        accumulator[formattedDate] = []
      }
      accumulator[formattedDate].push(date)
      return accumulator
    },
    {} as Record<string, T[]>,
  )
}

export const groupByRelativeDate = <T>(dates: T[], key: keyof T) => {
  const now = DateTime.now()
  const today = now.startOf('day')
  const yesterday = today.minus({ days: 1 })

  return dates.reduce(
    (accumulator: Record<string, T[]>, date: T) => {
      const dateTime = parseDate(date[key] as unknown as DateType)
      const dateStartOfDay = dateTime.startOf('day')

      let groupKey: string
      if (dateStartOfDay.hasSame(today, 'day')) {
        groupKey = 'Today'
      } else if (dateStartOfDay.hasSame(yesterday, 'day')) {
        groupKey = 'Yesterday'
      } else {
        groupKey = formatDate(date[key] as unknown as DateType, 'MMMM d, yyyy')
      }

      if (!accumulator[groupKey]) {
        accumulator[groupKey] = []
      }
      accumulator[groupKey].push(date)
      return accumulator
    },
    {} as Record<string, T[]>,
  )
}
